import { useQueryClient, } from "@tanstack/react-query";
import { ChevronRightIcon, DocumentIcon, } from "@heroicons/react/24/outline";
import { Box, Button, Heading, HStack, Icon, Link, Stack, Text, } from "@chakra-ui/react";

import { ProfileTextCell, } from "./ProfileTextCell";
import { ProfilePageHeader, } from "./ProfilePageHeader";
import { DEV, MODE, VITE_APP_VERSION, } from "../../../ENV.ts";
import { documentUrlByIdQueryConfig, IUserProfileDocumentItem, } from "../../../api";

interface IProfilePageView {
  isFetching: boolean;
  userData: {
    phone: string | undefined;
    email: string | undefined;
    fullName: string;
    birthdate: string;
    nationality: string;
    passportSeries?: string;
    passportNumber?: string;
    passportIssueDate: string;
    passportDivisionCode: string;
    registrationAddressString: string;
    moderatorQR?: string;
  };
  documents: {
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    data: IUserProfileDocumentItem[] | undefined;
    refetch?: () => Promise<unknown>;
  };
  handleLogout: () => void;
}

export function ProfilePageView({ isFetching, userData, documents, handleLogout, }: IProfilePageView,) {
  const queryClient = useQueryClient();

  const handleOpenDocument = async (id: string,): Promise<void> => {
    try {
      const data = await queryClient.fetchQuery(documentUrlByIdQueryConfig(id,),);
      if (data.url) {
        window.location.assign(data.url,);
      }
    } catch (_e) {
      // noop
    }
  };

  return (
    <div id="profilePage">
      <ProfilePageHeader />

      <Box>
        <Heading pt={8} pb={2} fontSize="xl">Контакты</Heading>
        <Stack spacing={4} py={4}>
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Телефон"
            text={userData.phone}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Email"
            text={userData.email}
          />
        </Stack>
      </Box>

      <Box>
        <Heading pt={8} pb={2} fontSize="xl">Личные данные</Heading>
        <Stack spacing={4} py={4}>
          <ProfileTextCell
            isLoaded={!isFetching}
            label="ФИО"
            text={userData.fullName}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Дата рождения"
            text={userData.birthdate}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Гражданство"
            text={userData.nationality}
          />
          {userData.passportSeries && userData.passportNumber && (
            <ProfileTextCell
              isLoaded={!isFetching}
              label="Серия и номер паспорта"
              text={`${userData.passportSeries} ${userData.passportNumber}`}
            />
          )}
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Дата выдачи паспорта"
            text={userData.passportIssueDate}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Код подразделения"
            text={userData.passportDivisionCode}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="Адрес регистрации"
            text={userData.registrationAddressString}
          />
          <ProfileTextCell
            isLoaded={!isFetching}
            label="QR"
            text={userData.moderatorQR}
          />
        </Stack>
      </Box>

      {(documents.isFetching || documents.isError || documents.isSuccess) && (
        <Box>
          <Heading pt={8} pb={2} fontSize="xl">Документы</Heading>
          {documents.isFetching && (
            <ProfileTextCell
              isLoaded={false}
              label="-"
              text="-"
            />
          )}

          {documents.isSuccess && (
            <Stack spacing={4} py={4}>
              {documents.data?.map(document => (
                <HStack key={document.id} alignItems="flex-start">
                  <Icon as={DocumentIcon} boxSize={6} />
                  <Link
                    color="primary"
                    onClick={() => {
                      void handleOpenDocument(document.id,);
                    }}
                  >
                    {document.name}
                  </Link>
                  <Icon as={ChevronRightIcon} boxSize={4} marginLeft="auto" />
                </HStack>
              ),)}
            </Stack>
          )}

          {documents.isError && (
            <HStack py={4} alignItems="flex-start">
              <Text color="text">Не удалось загрузить</Text>
              <Box flex="1 1 0" />
              <Link colorScheme="primary" onClick={() => { void documents.refetch?.(); }}>
                Повторить
              </Link>
            </HStack>
          )}
        </Box>
      )}

      {(MODE === "develop" || DEV) && (
        <Box pt={10}>
          <ProfileTextCell
            label="Версия приложения:"
                        // in local development mode, import.meta.env.VITE_APP_VERSION is not defined
                        // in test environment it will be set to the short commit sha
            text={VITE_APP_VERSION ?? "Не задана"}
          />
        </Box>
      )}

      {(MODE === "develop" || DEV) && (
        <Box pt={10}>
          <Button onClick={() => {
            throw new Error("Sentry Example Error",);
          }}
          >
            Generate Test Errors
          </Button>
        </Box>
      )}
      <Box pt={10} pb={4}>
        <Button w="100%" onClick={handleLogout}>
          Выйти
        </Button>
      </Box>
    </div>
  );
}
